import { useEffect } from 'react';
import * as WebFont from 'webfontloader';
import type { CustomerThemeOverrides } from '@services/tenantConfig.types';

// Fonts loaded statically by ThemeWrapper — skip requesting them again.
const STATIC_FONTS = new Set([
  'Roboto',
  'Montserrat',
  'Inter',
  'Gazpacho',
  'Gazpacho Black',
  'Agrandir',
]);
// Generic CSS keywords that are not loadable font families.
const GENERIC_FAMILIES = new Set(['sans-serif', 'serif', 'monospace', 'system-ui', 'cursive', 'fantasy']);

// Module-level caches mirror the browser's loaded-font set across re-renders.
// Fonts are document-global, so sharing this state across instances is correct.
const requested = new Set<string>();
const loadedCustomFonts = new Set<string>();

// fontStacks keys where the family name and its uploaded file URL are paired.
const CUSTOM_FONT_PAIRS = [
  ['displayFont', 'displayFontUrl'],
  ['headerFont', 'headerFontUrl'],
  ['bodyFont', 'bodyFontUrl'],
  ['monoFont', 'monoFontUrl'],
] as const;

const parseDynamicFonts = (fontFamily: string): string[] =>
  fontFamily
    .split(',')
    .map((f) => f.trim().replace(/^["']|["']$/g, ''))
    .filter((f) => f && !STATIC_FONTS.has(f) && !GENERIC_FAMILIES.has(f) && !requested.has(f));

const extractFontFamily = (overrides: CustomerThemeOverrides): string | undefined => {
  const muiTypography = overrides.muiOptions?.typography;
  if (muiTypography && typeof muiTypography === 'object' && 'fontFamily' in muiTypography) {
    const ff = (muiTypography as Record<string, unknown>).fontFamily;
    if (typeof ff === 'string') return ff;
  }
  return overrides.fontStacks?.bodyFont;
};

const loadCustomFontFaces = (overrides: CustomerThemeOverrides): void => {
  const stacks = (overrides as Record<string, unknown>).fontStacks;
  if (!stacks || typeof stacks !== 'object') return;
  const s = stacks as Record<string, unknown>;

  for (const [familyKey, urlKey] of CUSTOM_FONT_PAIRS) {
    const family = s[familyKey];
    const url = s[urlKey];
    if (typeof family !== 'string' || typeof url !== 'string') continue;
    if (!url.startsWith('http') && !url.startsWith('/')) continue;
    if (loadedCustomFonts.has(family)) continue;

    loadedCustomFonts.add(family);
    new FontFace(family, `url(${url})`)
      .load()
      .then((face) => document.fonts.add(face))
      .catch((err) => console.warn(`Failed to load custom font "${family}":`, err));
  }
};

/**
 * Loads fonts for the tenant config:
 * - Custom uploaded fonts (UUID.ext URLs) via the FontFace API.
 * - Google Fonts for body/MUI typography via WebFont.load.
 *
 * Intentionally idempotent: each unique family is requested at most once
 * per page session (the browser's font cache handles the rest).
 */
export const useDynamicFonts = (overrides: CustomerThemeOverrides | undefined): void => {
  const fontFamily = overrides ? extractFontFamily(overrides) : undefined;

  // Stable string dep — only changes when the actual font URLs change, not on reference identity.
  const stacks = overrides ? ((overrides as Record<string, unknown>).fontStacks as Record<string, unknown> | undefined) : undefined;
  const customFontSig = stacks
    ? CUSTOM_FONT_PAIRS.map(([, urlKey]) => stacks[urlKey] ?? '').join(',')
    : '';

  useEffect(() => {
    if (overrides) loadCustomFontFaces(overrides);
  }, [customFontSig]);

  useEffect(() => {
    if (!fontFamily) return;
    const newFonts = parseDynamicFonts(fontFamily);
    if (!newFonts.length) return;
    newFonts.forEach((f) => requested.add(f));
    WebFont.load({ google: { families: newFonts } });
  }, [fontFamily]);
};
